/**
 * Implementation that can be used to store nested entities that use the markedForDelete pattern.
 *
 * In addition, sync() can be successfully called and will checkpoint the state of things for use with cancelChanges() and localData().
 *
 * This is implemented by utilizing offline mode of the datasource.  This means the a model is required and it must have an id field defined.
 *
 * Methods:
 * localData(): getter/setter for state backing the datasource.  Uses markedForDelete field
 * offlineData(): Kendo method that backs localData().  Probably shouldn't be used directly.
 * willHaveDataAfterPersist(): Determine if the current synced state has items.
 * seek(): query() call that resets filters
 */

/**
 * This function generates a custom mixin for NestedData functionality.
 * This is used because kendo has multiple datasource types that could have nested data variants.
 * @param superDS Class of parent datasource type
 * @private
 */
var _nestedMixin = function(superDS) {
    return {
        init: function(config) {
            config.offlineStorage = {
                getItem: $.proxy(this._getInternal, this),
                setItem: $.proxy(this._storeInternal, this)
            };
            superDS.fn.init.call(this, config);
            this.online(false);
            if (config.data) {
                this.localData(config.data);
            }
        },

        /** Internal Data stores a JSON representation of the current synced state.
         * It is JSON to ensure that all references must be copies.
         */
        _internalData: "[]",

        /**
         * Store the internal state.
         * @private
         */
        _storeInternal: function(item) {
            this._internalData = JSON.stringify(item);
        },

        /**
         * Get the internal state.
         * @private
         */
        _getInternal: function() {
            return JSON.parse(this._internalData);
        },

        /**
         * Overrides an internal kendo method.
         * @override
         */
        success: function() {
            if (!this.online()) {
                //This works around a kendo bug.  Kendo is not clearing out deleted elements on read()
                //Reported to telerik: #943838
                //TODO: when that is fixed, this work around should be removed
                this._destroyed = [];
            }
            return superDS.fn.success.apply(this, arguments);
        },

        /**
         * Override the read() kendo method
         */
        read: function() {
            var res = superDS.fn.read.apply(this, arguments);
            //Fixes a bug where _pristineData gets out of sync when read();read(); is called
            //sync() correctly sets pristineData.  It should always be safe to do a sync() right after a read()
            //TODO: remove this workaround once telerik fixes the root issue
            this.syncDuringReadInProgress = true;
            this.sync();
            this.syncDuringReadInProgress = false;
            return res;
        },

        /**
         * Override the cancelChanges() kendo method
         */
        cancelChanges: function(item) {
            if (item instanceof kendo.data.Model) {
                return superDS.fn.cancelChanges.apply(this, arguments);
            } else {
                //Mantis: #37804 - Fixes a bug where calling read(),remove(),sync(),cancelChanges() causes the delete to be undone
                //TODO: This should be removable once telerik fixes the root issue
                this.read();
            }
        },

        _needsOrdinal() {
            return this.options.schema &&
                   this.options.schema.model &&
                   this.options.schema.model.fields &&
                   this.options.schema.model.fields.ordinal &&
                   this.options.schema.model.fields.ordinal.calculated;
        },

        _synthesizeOrdinal(data) {
            if(this._needsOrdinal()) {
                return data.map((item, ordinal) => ({...item, ordinal}));
            } else {
                return data;
            }
        },

        _clearOrdinal(data) {
            if(this._needsOrdinal()) {
                return data
                    .sort((item1, item2) => item1.ordinal - item2.ordinal)
                    .map((item) => {
                       delete item.ordinal;
                       return item;
                    });
            } else {
                return data;
            }
        },

       _clearMarkedForDelete(data) {
            return data.map((item) => {
                const clone = JSON.parse(JSON.stringify(item));
                if(item.markedForDelete) {
                    clone.__state__ = "destroy";
                    delete clone.markedForDelete;
                }
                return clone;
            });
        },

        _synthesizeMarkedForDelete(data) {
            return data.map((item) => {
                if (item.__state__ === "destroy") {
                    item.markedForDelete = true;
                }
                delete item.__state__;
                delete item.uid;
                return item;
            }).filter((item) => {
                return this.options.useMarkedForDelete || !item.markedForDelete;
            });
        },



        /**
         * Getter or setter for the data backing this instance.
         * This is a wrapper around offlineData, but it handles markedForDelete flags and gets rid of __state__ and uid fields.
         */
        localData: function(data) {
            if(arguments.length) {
                this.offlineData(this._synthesizeOrdinal(this._clearMarkedForDelete(data || [])));
            } else {
                return this._clearOrdinal(this._synthesizeMarkedForDelete(this.offlineData()));
            }
        },

        /**
         * Override the default datasource data() method.  This may only be called as a getter.
         */
        data: function(data) {
            if (data !== undefined) {
                throw new Error("NestedData does not support using data() as a setter.  Use localData instead.");
            } else {
                return superDS.fn.data.call(this);
            }
        },

        /**
         * Allows NestedData to play nicely with SnapshotList.  Seek in this case just resets filters.
         * There is not currently support for initial filters.
         */
        seek: function() {
            var go = $.proxy(function(page) {
                return this.query({
                    filter: {},
                    sort: this.sort(),
                    pageSize: this.pageSize(),
                    page: parseInt(page)
                });
            }, this);
            return go(0);
        },

        /**
         * Determine if there would be any non-deleted items if the current localData() was persisted right now(without syncing).
         * @returns {boolean} True if there are persisted, or ready to be persisted, items in localData()
         */
        willHaveDataAfterPersist: function() {
            return this.offlineData().some(function(item) {
                return !(item.__state__ === "destroy");
            });
        },

        /**
         * Returns all items that have been deleted but not yet synced.  Specific use case: prompt lists must send the
         * current values to the server in calls to render a form, AND must not call sync in order to do so.
         * @return {*}
         */
        getDeletedDataToBePersisted: function() {
            // look at localData.  Find each item in data.  If not found, it was deleted.
            var me = this,
                idField = this.options.schema.model.idField;
            return this.localData().filter(function(localItem) {
                return localItem[idField] && !me.data().some(function(current) {
                    return localItem[idField] === current[idField];
                });
            });
        }
    }
};
var result = kendo.data.DataSource.extend(_nestedMixin(kendo.data.DataSource));
result._nestedMixin = _nestedMixin;
export default result;
